import {
  ISensitivityWorkbench,
  ISimulationAssumptionRow,
  ISimulationDriver,
  ISimulationIssueRow,
  ISimulationManagerWorkbench,
  ISimulationWorkbench,
} from './simulation.models';

const bool = (value: any, fallback = false) =>
  value === undefined || value === null ? fallback : Boolean(value);

const num = (value: any, fallback = 0) =>
  Number.isFinite(Number(value)) ? Number(value) : fallback;

const arr = (value: any): any[] =>
  Array.isArray(value) ? value : Array.isArray(value?.rows) ? value.rows : [];

export const normalizeSimulationManager = (response: any): ISimulationManagerWorkbench => {
  const sd = response?.sd ?? response ?? {};
  const context = sd.context ?? {};
  const summary = sd.summary ?? {};
  const actions = sd.actions ?? {};

  return {
    context: {
      ...context,
      planningcycleid: num(context.planningcycleid ?? context.pci),
      planningcyclecode: context.planningcyclecode ?? context.pcc ?? '',
      planningcyclename: context.planningcyclename ?? context.pcn ?? '',
      phasecode: context.phasecode ?? 'SIMULATION',
      forecastbatchid: context.forecastbatchid ? num(context.forecastbatchid) : undefined,
    },
    summary: {
      scenariocount: num(summary.scenariocount),
      baselinecount: num(summary.baselinecount),
      draftcount: num(summary.draftcount),
      submittedcount: num(summary.submittedcount),
      approvedcount: num(summary.approvedcount),
      publishedforecastavailable: bool(summary.publishedforecastavailable),
    },
    scenarios: arr(sd.scenarios)
      .map((row: any) => ({
        ...row,
        rid: num(row.rid),
        baseline: bool(row.baseline),
        parentid: row.parentid == null ? null : num(row.parentid),
        forecastbatchid: row.forecastbatchid == null ? undefined : num(row.forecastbatchid),
        basisid: row.basisid == null ? undefined : num(row.basisid),
      }))
      // Analytical OGAP/SENS workspaces are application-owned and opaque to planners.
      .filter((row: any) => !row.typecode || String(row.typecode).toUpperCase() === 'PLAN'),
    basis: arr(sd.basis ?? sd.basisoptions ?? sd.scenariobasis).map((row: any) => ({
      rid: num(row.rid ?? row.recid),
      code: row.code ?? row.shc ?? row.shortcode ?? '',
      name: row.name ?? row.nam ?? row.recname ?? row.code ?? row.shc ?? '',
      description: row.description ?? row.dsc,
      active: bool(row.active ?? row.status, true),
    })),
    forecastbatches: arr(sd.forecastbatches ?? sd.forecasts ?? sd.batchoptions).map((row: any) => ({
      rid: num(row.forecastbatchid ?? row.rid ?? row.recid),
      code: row.forecastbatchcode ?? row.code ?? row.shc ?? row.shortcode ?? '',
      name: row.forecastbatchname ?? row.name ?? row.nam ?? row.recname ?? row.code ?? row.shc ?? '',
      publishedat: row.publishedat ?? row.published,
      planningcycleid: row.planningcycleid == null ? undefined : num(row.planningcycleid),
    })),
    actions: {
      canRefresh: bool(actions.canRefresh, true),
      canCreateScenario: bool(actions.canCreateScenario),
      canOpenScenario: bool(actions.canOpenScenario),
      canComplete: bool(actions.canComplete),
      nextpage: actions.nextpage ?? 'executivereview',
    },
    guidance: sd.guidance ?? {},
  };
};

const normalizeAssumption = (row: any): ISimulationAssumptionRow => ({
  ...row,
  id: row.id ?? row.rid ?? row.rix ?? row.code,
  rid: row.rid == null ? undefined : num(row.rid),
  category: row.category ?? 'GENERAL',
  code: row.code ?? row.shc ?? '',
  name: row.name ?? row.nam ?? row.code ?? '',
  value: row.value == null ? null : num(row.value),
  confidence: row.confidence == null ? null : num(row.confidence),
  required: bool(row.required),
  status: row.status ?? 1,
  sourceclass: row.sourceclass ?? row.extrainfo?.sourceclass,
  sourcechannel: row.sourcechannel ?? row.extrainfo?.sourcechannel,
  extrainfo: row.extrainfo ?? {},
});

const normalizeDriver = (row: any): ISimulationDriver => ({
  ...row,
  driverid: row.driverid == null ? undefined : num(row.driverid),
  driverix: row.driverix ?? row.scenariovalueix,
  drivercode: row.drivercode ?? row.code ?? '',
  drivername: row.drivername ?? row.name ?? row.drivercode ?? '',
  value: num(row.value ?? row.effectivevalue),
  effectivevalue: row.effectivevalue == null ? undefined : num(row.effectivevalue),
  baselinevalue: row.baselinevalue == null ? undefined : num(row.baselinevalue),
  minvalue: row.minvalue == null ? undefined : num(row.minvalue),
  maxvalue: row.maxvalue == null ? undefined : num(row.maxvalue),
  stepvalue: row.stepvalue == null ? undefined : num(row.stepvalue),

  // Current Scenario provenance: never infer this from Driver defaults.
  sourcecode: row.sourcecode,
  sourceclass: row.sourceclass ?? row.extrainfo?.sourceclass,
  sourcechannel: row.sourcechannel ?? row.extrainfo?.sourcechannel,

  // Registry/default provenance.
  sourcecategory: row.sourcecategory,
  defaultsource: row.defaultsource,
  readinessgroup: row.readinessgroup,

  isrequired: bool(row.isrequired),
  isadjustable: bool(row.isadjustable, true),
  allowmanualoverride: bool(row.allowmanualoverride, true),
});

const normalizeIssue = (row: any, index: number): ISimulationIssueRow => ({
  ...row,
  id: row.id ?? row.rid ?? row.rix ?? `${row.issuecode ?? row.code ?? 'ISSUE'}-${index}`,
  severity: row.severity ?? row.svr ?? 'INFO',
  issuecode: row.issuecode ?? row.code ?? '',
  issuename: row.issuename ?? row.name ?? row.message ?? row.issuecode ?? 'Simulation issue',
});

export const normalizeSimulationWorkbench = (response: any): ISimulationWorkbench => {
  const sd = response?.sd ?? response ?? {};
  const baseContext = sd.context ?? {};
  const scenario = sd.scenario ?? {};
  const actions = sd.actions ?? {};
  const executive = sd.executive ?? {};
  const readiness = sd.readiness ?? {};
  const executiveReturn = sd.executivereturn ?? {};
  const submission = sd.submission ?? {};
  const issues = arr(sd.issues).map(normalizeIssue);

  const blockingcount = num(
    readiness.blockingcount,
    issues.filter((x) => ['BLOCKING', 'ERROR', 'CRITICAL']
      .includes(String(x.severity).toUpperCase())).length,
  );
  const warningcount = num(
    readiness.warningcount,
    issues.filter((x) => String(x.severity).toUpperCase() === 'WARNING').length,
  );

  const statecode = scenario.statecode ?? baseContext.statecode ?? 'DRAFT';
  const canEdit = bool(actions.canEditAssumptions ?? actions.canEdit);

  const assumptions = arr(sd.assumptions).map(normalizeAssumption);
  const driverPayload = sd.drivers ?? sd.mixer ?? {};
  const drivers = arr(driverPayload.drivers ?? driverPayload).map(normalizeDriver);
  const sensitivityPayload = sd.sensitivity ?? {};

  return {
    context: {
      ...baseContext,
      planningcycleid: num(baseContext.planningcycleid ?? baseContext.pci),
      planningcyclecode: baseContext.planningcyclecode ?? baseContext.pcc ?? '',
      planningcyclename: baseContext.planningcyclename ?? baseContext.pcn ?? '',
      scenarioid: num(scenario.rid ?? baseContext.scenarioid ?? baseContext.sci),
      scenarioix: scenario.rix ?? baseContext.scenarioix,
      scenariocode: scenario.shc ?? baseContext.scenariocode ?? '',
      scenarioname: scenario.nam ?? baseContext.scenarioname ?? '',
      phasecode: baseContext.phasecode ?? 'SIMULATION',
      statecode,
      forecastbatchid: scenario.forecastbatchid ?? baseContext.forecastbatchid,
      baseline: bool(scenario.baseline),
      parentscenarioid: scenario.parentid ?? null,
    },
    activeTab: 'executive',
    readiness: {
      ready: bool(readiness.ready),
      stale: bool(readiness.stale),
      score: num(readiness.score),
      blockingcount,
      warningcount,
      message: readiness.message ?? '',
      assumptionsready: bool(readiness.assumptionsready),
      forecastready: bool(readiness.forecastready),
      formulasready: bool(readiness.formulasready),
      constraintsready: bool(readiness.constraintsready),
      capacityready: bool(readiness.capacityready),
      sensitivityready: bool(readiness.sensitivityready),
      requiresrecompute: bool(readiness.requiresrecompute),
      hascomputed: bool(readiness.hascomputed),
      computationcurrent: bool(readiness.computationcurrent),
    } as any,
    actions: {
      canRefresh: bool(actions.canRefresh, true),
      canEditAssumptions: bool(actions.canEditAssumptions, canEdit),
      canUpdateDrivers: bool(actions.canUpdateDrivers, canEdit),
      canRecompute: bool(actions.canRecompute),
      canRunSensitivity: bool(actions.canRunSensitivity),
      canChangeTargetSet: bool(actions.canChangeTargetSet),
      canSubmitExecutiveReview: bool(actions.canSubmitExecutiveReview ?? actions.canSubmit),
      canResubmitExecutiveReview: bool(actions.canResubmitExecutiveReview),
      canOpenExecutiveReview: bool(actions.canOpenExecutiveReview),
      canAcknowledgeReturn: bool(actions.canAcknowledgeReturn),
      canExit: true,
      canReturnToManager: bool(actions.canReturnToManager, true),
      nextpage: actions.nextpage ?? 'executivereview',
    },
    executive,
    assumptions,
    mixer: {
      drivers,
      stale: bool(readiness.stale),
    },
    financials: sd.financials ?? {},
    objectives: arr(sd.objectives),
    targetsets: arr(sd.targetsets),
    sensitivity: normalizeSensitivityWorkbench(sensitivityPayload),
    issues,
    execution: sd.execution ?? {},
    submission: {
      ...submission,
      submitted: bool(submission.submitted),
      hassubmission: bool(submission.hassubmission),
      submissionversion: submission.submissionversion == null
        ? undefined
        : num(submission.submissionversion),
      executiveReviewId: submission.executiveReviewId == null
        ? undefined
        : num(submission.executiveReviewId),
      returnFeedback: submission.returnFeedback ?? null,
      recommended: bool(submission.recommended),
    },
    executivereturn: {
      returned: bool(executiveReturn.returned),
      reviewid: executiveReturn.reviewid == null ? undefined : num(executiveReturn.reviewid),
      reviewix: executiveReturn.reviewix,
      category: executiveReturn.category,
      reason: executiveReturn.reason,
      returnedby: executiveReturn.returnedby == null ? undefined : num(executiveReturn.returnedby),
      returnedat: executiveReturn.returnedat,
      acknowledged: bool(executiveReturn.acknowledged),
      acknowledgedby: executiveReturn.acknowledgedby == null
        ? undefined
        : num(executiveReturn.acknowledgedby),
      acknowledgedat: executiveReturn.acknowledgedat,
      revisionallowed: bool(executiveReturn.revisionallowed),
      revisionactive: bool(executiveReturn.revisionactive),
      resubmissionready: bool(executiveReturn.resubmissionready),
    },
  };
};

const normalizeSensitivityWorkbench = (payload: any): ISensitivityWorkbench => {
  const sd = payload?.sd ?? payload ?? {};

  const records = arr(sd.records).map((row: any, index: number) => ({
    ...row,

    id:
      row.id ??
      row.resultid ??
      row.resultix ??
      `${row.runid ?? 'SEN'}-${row.drivercode ?? index}`,

    rank: Number(row.rank ?? 0),

    influencesharepercent:
      row.influencesharepercent == null
        ? null
        : Number(row.influencesharepercent),

    downsideimpact:
      row.downsideimpact == null
        ? null
        : Number(row.downsideimpact),

    upsideimpact:
      row.upsideimpact == null
        ? null
        : Number(row.upsideimpact),

    absoluteimpact:
      row.absoluteimpact == null
        ? null
        : Number(row.absoluteimpact),

    downsideelasticity:
      row.downsideelasticity == null
        ? null
        : Number(row.downsideelasticity),

    upsideelasticity:
      row.upsideelasticity == null
        ? null
        : Number(row.upsideelasticity),
  }));

  const skipped = arr(sd.skipped).map((row: any, index: number) => ({
    ...row,

    id:
      row.id ??
      row.driverid ??
      `${row.drivercode ?? 'SKIP'}-${index}`,
  }));

  return {
    scenario: sd.scenario,

    run: sd.run ?? null,

    summary: {
      hasresults: bool(sd.summary?.hasresults),
      evaluatedcount: num(sd.summary?.evaluatedcount),
      skippedcount: num(sd.summary?.skippedcount),
      totalabsoluteimpact: Number(sd.summary?.totalabsoluteimpact ?? 0),
      topdrivercode: sd.summary?.topdrivercode ?? null,
      ready: bool(sd.summary?.ready, true),
    },

    records,

    skipped,
  };
};
